import { Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { IMapComponent } from '../IMapComponent';
import { IMarkerPoint, IPoint, MarkerColor, MarkerSize } from '../IPoint';
import { ISegment } from '../ISegment';
import { ILabels } from 'libraries/interfaces/ILabels';
import { Yii } from 'libraries/interfaces/Yii';
import { GoogleMapsStyle, IGoogleMapsConfiguration } from './IGoogleMapsConfiguration';
import { GoogleMapApiLoaderService } from './googleMapApiLoader.service';
import { IGoogleMaps } from './IGoogleMaps';
import { BLUE } from '../Colors';
import { ICenterRadiusBounds, IMapBounds, IMinimumBounds } from '../IMapBounds';
import { MapComputationService } from '../computation/map-computation.service';
import { ErrorTracingService } from "libraries/service/error-tracing/error-tracing.service";
import { RuntimeError } from "libraries/service/error-tracing/runtime-error.model";

const DEFAULT_CENTER: IPoint = {
    latitude: 46.8167,
    longitude: 1.7,
};

interface IInitalizedMap {
    maps: IGoogleMaps;
    map: google.maps.Map;
}

@Component({
    selector: 'google-map',
    template: `
        <notification type="error" *ngIf="!configuration || !configuration.apiKey">{{ labels.noConfiguration }}</notification>
        <notification type="error" *ngIf="error">{{ error }}</notification>
        <div #container
             *ngIf="configuration?.apiKey"
             class="mapContainer"></div>
        <input #search
               *ngIf="activateSearch"
               [ngStyle]="{'visible': this.map}"
               [(ngModel)]="initialSearchText"
               type="text"
               class="text-field"
               [placeholder]="labels.placeholder"
               (keydown.enter)="onInputEnter($event)"/>
    `,
    styles: [`
        :host {
            display: block;
            position: relative;
        }

        div.mapContainer{
            position:absolute;
            width: 100%;
            height: 100%;
            z-index: 1;
        }

        input{
            padding: 0.5rem;
            border: 1px solid #e6eaed;
            outline: none;
            margin: 10px;
            width: 20em;
            font-family: Roboto, Arial, sans-serif;
            height: 40px;
            font-size: 16px;
        }
    `]
})
export class GoogleMapComponent implements OnInit, IMapComponent, OnChanges, OnDestroy {
    @Input()
    public defaultCenter: IPoint;

    @Input()
    public defaultBounds: IMinimumBounds | ICenterRadiusBounds;

    @Input()
    public points: IMarkerPoint[];

    @Input()
    public segments: ISegment[];

    @Input()
    public configuration: IGoogleMapsConfiguration;

    @Input()
    public activateSearch: boolean;

    @Input()
    public searchText: string;

    @Input()
    public defaultZoom: number = 5;

    @Output()
    public readonly onMarkerClick: EventEmitter<IMarkerPoint> = new EventEmitter();

    @Output()
    public readonly onBoundsChanged: EventEmitter<IMapBounds> = new EventEmitter();

    @ViewChild('container')
    public container: ElementRef;

    @ViewChild('search')
    public searchInput: ElementRef;

    public error: string;
    public initialSearchText: string;

    public readonly labels: ILabels = {
        noConfiguration: Yii.t("wui-shared-js", "Aucune clé d'API google maps définie"),
        placeholder: Yii.t("wui-shared-js", "Rechercher un lieu")
    };

    public map: google.maps.Map;

    private apiLoading: Promise<IInitalizedMap>;
    private markers: google.maps.Marker[];
    private lines: google.maps.Polyline[];
    private searchBox: google.maps.places.SearchBox;

    constructor(
        private readonly apiLoader: GoogleMapApiLoaderService,
        private readonly mapComputationService: MapComputationService,
        private readonly errorTracingService: ErrorTracingService,
    ) {
    }

    public ngOnInit(): void {
        this.initialSearchText = this.searchText;
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (this.configuration && this.configuration.apiKey && !this.apiLoading) {
            this.apiLoading = this.apiLoader.load(this.configuration.apiKey)
                .then((maps: IGoogleMaps): IInitalizedMap => {
                    let mapTypeId: google.maps.MapTypeId;
                    switch (this.configuration.mapStyle) {
                        case GoogleMapsStyle.Terrain:
                            mapTypeId = maps.MapTypeId.TERRAIN;
                            break;
                        case GoogleMapsStyle.Hybrid:
                            mapTypeId = maps.MapTypeId.HYBRID;
                            break;
                        case GoogleMapsStyle.Satellite:
                            mapTypeId = maps.MapTypeId.SATELLITE;
                            break;
                        default:
                            mapTypeId = maps.MapTypeId.ROADMAP;
                    }

                    this.map = new maps.Map(this.container.nativeElement, {
                        streetViewControl: false,
                        rotateControl: false,
                        fullscreenControl: false,
                        scaleControl: true,
                        mapTypeControl: false,
                        zoomControlOptions: {
                            position: maps.ControlPosition.TOP_RIGHT
                        },
                        mapTypeId,
                        styles: [
                            {
                                featureType: "poi",
                                elementType: "labels",
                                stylers: [
                                    { visibility: "off" }
                                ]
                            }
                        ]
                    });

                    if (this.defaultBounds && (this.defaultBounds as IMinimumBounds).ne) {
                        fitBounds({ maps, map: this.map }, this.defaultBounds as IMinimumBounds);
                    } else if (this.defaultBounds && (this.defaultBounds as ICenterRadiusBounds).center) {
                        const bounds: ICenterRadiusBounds = this.defaultBounds as ICenterRadiusBounds;

                        const east: IPoint = this.mapComputationService.computeOffset(
                            bounds.center,
                            90,
                            bounds.radiusInMeters,
                        );
                        const west: IPoint = this.mapComputationService.computeOffset(
                            bounds.center,
                            270,
                            bounds.radiusInMeters,
                        );

                        this.map.fitBounds(
                            new maps.LatLngBounds(
                                new maps.LatLng(west.latitude, west.longitude),
                                new maps.LatLng(east.latitude, east.longitude),
                            )
                        );
                    } else {
                        const defaultCenter: IPoint = this.defaultCenter || DEFAULT_CENTER;

                        this.map.setCenter(new maps.LatLng(defaultCenter.latitude, defaultCenter.longitude));
                        this.map.setZoom(this.defaultZoom);
                    }

                    this.map.addListener("bounds_changed", () => {
                        this.boundsChanged({ maps, map: this.map });
                    });

                    return { maps, map: this.map };
                })
                .then((init: IInitalizedMap) => {
                    this.autoZoom(init);
                    return init;
                })
                .catch((error: Error) => {
                    this.onError(error);
                    throw error;
                });
        }

        if (!this.apiLoading) {
            return;
        }

        if (changes.points && changes.points.currentValue) {
            this.apiLoading
                .then((init: IInitalizedMap) => { this.generateMarkers(init); return init; })
                .then((init: IInitalizedMap) => {
                    if(this.mapComputationService.haveChangedPoints(changes.points.previousValue, changes.points.currentValue)){
                        this.autoZoom(init);
                    }
                })
                .catch((error: Error) => {
                    this.onError(error);
                });
        }

        if (changes.segments) {
            this.apiLoading
                .then((init: IInitalizedMap) => { this.generateSegments(init); return init; })
                .catch((error: Error) => {
                    this.onError(error);
                });
        }

        if (changes.activateSearch) {
            this.apiLoading
                .then((init: IInitalizedMap) => { this.changeSearchActivation(init); });

        }
    }

    public ngOnDestroy(): void {
        // https://stackoverflow.com/questions/10485582/what-is-the-proper-way-to-destroy-a-map-instance
        this.map = null;
        this.apiLoading = null;
        this.markers = null;
        this.lines = null;
        this.searchBox = null;
    }

    public invalidateSize(): void {
        // Nothing to do
    }

    public onInputEnter(event: KeyboardEvent): void {
        event.preventDefault();
    }

    private generateMarkers({ maps, map }: IInitalizedMap): void {
        if (this.markers) {
            this.markers.forEach((marker: google.maps.Marker) => { marker.setMap(null); });
        }

        this.markers = (this.points || []).map((point: IMarkerPoint): google.maps.Marker => {

            let icon: google.maps.Icon = undefined;

            let text: string = point.label ? encodeURIComponent(point.label) : "%E2%80%A2";
            const font: string = point.label ? "fonts/Roboto-Regular.ttf" : "fonts/arialuni_t.ttf";
            let iconName: string = "spotlight-waypoint-blue.png";
            const size: number = point.label && point.label.length === 1 ? 16 : 14;

            switch (point.color) {
                case MarkerColor.Green:
                    iconName = "spotlight-waypoint-a.png";
                    break;
                case MarkerColor.Red:
                    iconName = point.label ? `spotlight-waypoint-b.png` : "spotlight-poi.png";
                    text = point.label ? text : "";
                    break;
                case MarkerColor.Blue:
                default:
                    text = "";
            }

            const scale: number = point.size === MarkerSize.Large ? 1.5 : 1;

            const url: string = `https://mt.google.com/vt/icon?psize=${size}&font=${font}&color=ff330000&name=icons/spotlight/${iconName}&ax=44&ay=48&scale=${scale}&text=${text}`;

            // Source: https://stackoverflow.com/questions/17746740/google-map-icons-with-visualrefresh
            // This solution does not allow to add text on blue markers
            // Another solution based on :
            // Source : http://tancro.e-central.tv/grandmaster/markers/google-icons/mapfiles-ms-micons.html
            // allows to do that, but using labels within markers is not right when markers
            // are close
            icon = {
                url,
            };

            const marker: google.maps.Marker = new maps.Marker({
                position: new maps.LatLng(point.latitude, point.longitude),
                title: point.label,
                icon
            });

            marker.addListener("click", () => {
                this.onMarkerClick.emit(point);
            });

            return marker;
        });

        this.markers.forEach((marker: google.maps.Marker) => {
            marker.setMap(map);
        });
    }

    private generateSegments({ map, maps }: IInitalizedMap): void {
        if (this.lines) {
            this.lines.forEach((line: google.maps.Polyline) => {
                line.setMap(null);
            });
        }

        this.lines = (this.segments || []).map((segment: ISegment): google.maps.Polyline => {
            const line: google.maps.Polyline = new maps.Polyline({
                path: [
                    { lat: segment.from.latitude, lng: segment.from.longitude },
                    { lat: segment.to.latitude, lng: segment.to.longitude },
                ],
                geodesic: false,
                strokeColor: `#${BLUE}`,
                strokeOpacity: 1,
                strokeWeight: 3
            });

            line.setMap(map);

            return line;
        });
    }

    private autoZoom({ maps, map }: IInitalizedMap): void {
        if (!this.points || !this.points.length) {
            return;
        }

        if (this.points && this.points.length > 1) {
            const autoBounds = this.points
                .map((point: IMarkerPoint) => new maps.LatLng(point.latitude, point.longitude))
                .reduce((bounds: google.maps.LatLngBounds, coordinates: google.maps.LatLng): google.maps.LatLngBounds => {
                    bounds.extend(coordinates);
                    return bounds;
                }, new maps.LatLngBounds());
            map.fitBounds(autoBounds);
        } else {
            const center: IPoint = this.points && this.points.length === 1 ? this.points[0] : this.defaultCenter || DEFAULT_CENTER;

            map.setZoom(this.defaultZoom);
            map.setCenter({ lat: center.latitude, lng: center.longitude });
        }
    }

    private onError(error: Error) {
        this.errorTracingService.captureException(new RuntimeError("Error while loading google maps api", error));
        this.error = Yii.t("wui-shared-js", "Erreur lors du chargement de Google Maps");
    }

    private boundsChanged({ maps, map }: IInitalizedMap): void {
        const bounds: google.maps.LatLngBounds = map.getBounds();

        if (isNaN(bounds.getNorthEast().lat())) {
            return;
        }

        const ne: IPoint = { latitude: bounds.getNorthEast().lat(), longitude: bounds.getNorthEast().lng() };
        const sw: IPoint = { latitude: bounds.getSouthWest().lat(), longitude: bounds.getSouthWest().lng() };
        const center: IPoint = { latitude: bounds.getCenter().lat(), longitude: bounds.getCenter().lng() };

        const ns: number = this.mapComputationService.computeDistance(ne, { latitude: sw.latitude, longitude: ne.longitude });
        const ew: number = this.mapComputationService.computeDistance(ne, { latitude: ne.latitude, longitude: sw.longitude });

        const mapBounds: IMapBounds = {
            ne,
            nw: { latitude: ne.latitude, longitude: sw.longitude },
            sw,
            se: { latitude: sw.latitude, longitude: ne.longitude },
            center: { latitude: center.latitude, longitude: center.longitude },
            distanceInMeters: {
                ns,
                ew,
            }
        };

        this.onBoundsChanged.emit(mapBounds);
    }

    private changeSearchActivation({ map, maps }: IInitalizedMap): void {
        if (!this.activateSearch) {
            let searchBoxIndex: number;
            map.controls[maps.ControlPosition.TOP_LEFT].forEach((node, index: number) => {
                if (node === (this.searchBox as any)) {
                    searchBoxIndex = index;
                }
            });

            if (searchBoxIndex !== undefined) {
                map.controls[maps.ControlPosition.TOP_LEFT].removeAt(searchBoxIndex);
            }
            this.searchBox = null;
            return;
        }

        map.controls[maps.ControlPosition.TOP_LEFT].push(this.searchInput.nativeElement);
        this.searchBox = new maps.places.SearchBox(this.searchInput.nativeElement);

        this.searchBox.addListener('places_changed', () => {
            if (!this.searchBox) {
                return;
            }

            const places: google.maps.places.PlaceResult[] = this.searchBox.getPlaces();
            this.fitToFirstResult({ maps, map }, places);
        });

        if (this.searchText) {
            const service: google.maps.places.PlacesService = new maps.places.PlacesService(map);
            service.findPlaceFromQuery({
                query: this.searchText,
                fields: ['name', 'geometry']
            }, (results: google.maps.places.PlaceResult[]) => {
                this.fitToFirstResult({ maps, map }, results);
            });
        }
    }

    private fitToFirstResult({ maps, map }: IInitalizedMap, places: google.maps.places.PlaceResult[]): void {
        if (places.length === 0 || !this.map) {
            return;
        }

        const ne: IPoint = {
            latitude: places[0].geometry.viewport.getNorthEast().lat(),
            longitude: places[0].geometry.viewport.getNorthEast().lng(),
        };

        const sw: IPoint = {
            latitude: places[0].geometry.viewport.getSouthWest().lat(),
            longitude: places[0].geometry.viewport.getSouthWest().lng(),
        };

        fitBounds({ maps, map }, { ne, sw });
    }
}

function fitBounds({ map, maps }: IInitalizedMap, bounds: IMinimumBounds): void {
    map.fitBounds(
        new maps.LatLngBounds(
            new maps.LatLng(bounds.sw.latitude, bounds.sw.longitude),
            new maps.LatLng(bounds.ne.latitude, bounds.ne.longitude),
        )
    );
}
